import { Button, Spinner } from "@chakra-ui/react";
import React from "react";
import AppModal from "components/modal/AppModal";
import { useIdioma } from "stores/idiomaStore";

/**
 * Respuesta comercial a una factura de compra.
 *
 * Las tres decisiones que permite el intercambio del SII. No es un formulario
 * cualquiera: lo que se elija acá se convierte en un XML firmado que le llega
 * al proveedor y no se puede retirar. Por eso hay un solo botón de confirmar,
 * dice exactamente qué va a pasar, y el motivo es obligatorio cuando la
 * decisión no es "aceptar" — un rechazo sin explicación deja al proveedor sin
 * saber qué corregir.
 */

const ACCIONES = [
  {
    clave: "aceptar",
    claveTitulo: "respuesta.aceptar",
    claveDetalle: "respuesta.aceptarDetalle",
    requiereMotivo: false,
    tono: "verde",
  },
  {
    clave: "aceptar_con_discrepancias",
    claveTitulo: "respuesta.discrepancias",
    claveDetalle: "respuesta.discrepanciasDetalle",
    requiereMotivo: true,
    tono: "ambar",
  },
  {
    clave: "rechazar",
    claveTitulo: "respuesta.rechazar",
    claveDetalle: "respuesta.rechazarDetalle",
    requiereMotivo: true,
    tono: "rojo",
  },
];

const TONOS = {
  verde: {
    activo: "border-emerald-500 bg-emerald-50 dark:border-emerald-400 dark:bg-emerald-400/10",
    punto: "bg-emerald-500",
  },
  ambar: {
    activo: "border-amber-500 bg-amber-50 dark:border-amber-400 dark:bg-amber-400/10",
    punto: "bg-amber-500",
  },
  rojo: {
    activo: "border-red-500 bg-red-50 dark:border-red-400 dark:bg-red-400/10",
    punto: "bg-red-500",
  },
};

export default function RespuestaComercialModal({
  isOpen,
  onClose,
  documento,
  enviando = false,
  onConfirmar,
}) {
  const { t } = useIdioma();
  const [accion, setAccion] = React.useState("aceptar");
  const [motivo, setMotivo] = React.useState("");

  // Cada vez que se abre para otro documento, se parte de cero: arrastrar el
  // motivo escrito para otra factura sería mandarle al proveedor una razón
  // que no corresponde.
  React.useEffect(() => {
    if (isOpen) {
      setAccion("aceptar");
      setMotivo("");
    }
  }, [isOpen, documento?.id]);

  const seleccionada = ACCIONES.find((a) => a.clave === accion) || ACCIONES[0];
  const motivoLimpio = motivo.trim();
  const faltaMotivo = seleccionada.requiereMotivo && !motivoLimpio;

  const confirmar = () => {
    if (faltaMotivo || enviando) return;
    onConfirmar({ accion, motivo: motivoLimpio });
  };

  const referencia = documento
    ? [documento.razon_social, documento.folio ? `#${documento.folio}` : ""]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <AppModal
      isOpen={isOpen}
      onClose={enviando ? () => {} : onClose}
      size="md"
      closeOnOverlayClick={!enviando}
      title={t("respuesta.titulo")}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onClose} isDisabled={enviando}>
            {t("accion.cancelar")}
          </Button>
          <Button
            size="sm"
            colorScheme={seleccionada.tono === "rojo" ? "red" : "brand"}
            onClick={confirmar}
            isDisabled={faltaMotivo || enviando}
          >
            {enviando ? (
              <span className="flex items-center gap-2">
                <Spinner size="xs" />
                {t("respuesta.enviando")}
              </span>
            ) : (
              t("respuesta.confirmar")
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {referencia ? (
          <p className="text-sm text-gray-600 dark:text-gray-300">{referencia}</p>
        ) : null}

        <div className="flex flex-col gap-2">
          {ACCIONES.map((op) => {
            const activa = op.clave === accion;
            const tono = TONOS[op.tono];
            return (
              <button
                key={op.clave}
                type="button"
                onClick={() => setAccion(op.clave)}
                aria-pressed={activa}
                disabled={enviando}
                className={[
                  "flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition",
                  "disabled:cursor-not-allowed disabled:opacity-60",
                  activa
                    ? tono.activo
                    : "border-gray-200 hover:bg-gray-50 dark:border-white/10 dark:hover:bg-white/5",
                ].join(" ")}
              >
                <span
                  className={[
                    "mt-1 h-2.5 w-2.5 shrink-0 rounded-full",
                    activa ? tono.punto : "bg-gray-300 dark:bg-white/20",
                  ].join(" ")}
                  aria-hidden="true"
                />
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-navy-700 dark:text-white">
                    {t(op.claveTitulo)}
                  </span>
                  <span className="text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                    {t(op.claveDetalle)}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
            {seleccionada.requiereMotivo
              ? t("respuesta.motivoObligatorio")
              : t("respuesta.motivoOpcional")}
          </span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            disabled={enviando}
            placeholder={t("respuesta.motivoAyuda")}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-navy-800 disabled:opacity-60 dark:border-white/15 dark:bg-navy-800 dark:text-white"
          />
          {faltaMotivo ? (
            <span className="text-xs text-red-600 dark:text-red-300">
              {t("respuesta.motivoFalta")}
            </span>
          ) : null}
        </label>

        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:bg-amber-400/10 dark:text-amber-200">
          {t("respuesta.advertencia")}
        </p>
      </div>
    </AppModal>
  );
}
